/**
 * Small noises, synthesised rather than shipped.
 *
 * Every avatar makes a sound when you pick it, because that was the specific
 * bit of Jackbox lightness asked for. They are generated with WebAudio
 * oscillators instead of audio files for a practical reason: twelve sound files
 * would outweigh the entire rest of this client, and a party game's first
 * impression should not be a loading spinner.
 *
 * Three rules, all of them about not being annoying:
 *
 *  - **Nothing plays until a user gesture.** Browsers block audio before one
 *    anyway, and a page that makes noise on load is the thing everyone hates.
 *  - **Failure is silent.** No audio device, an unsupported browser, a policy
 *    block - none of that should reach the player as an error.
 *  - **It can be turned off**, and the choice is remembered.
 */

const MUTE_KEY = 'cards.muted';

let context: AudioContext | null = null;

function audio(): AudioContext | null {
  if (muted()) return null;
  try {
    context ??= new AudioContext();
    // Created before a gesture, a context starts suspended; resuming inside the
    // gesture that called us is what actually makes it audible.
    if (context.state === 'suspended') void context.resume();
    return context;
  } catch {
    return null;
  }
}

export function muted(): boolean {
  try {
    return window.localStorage.getItem(MUTE_KEY) === 'true';
  } catch {
    return false;
  }
}

export function setMuted(value: boolean): void {
  try {
    window.localStorage.setItem(MUTE_KEY, value ? 'true' : 'false');
  } catch {
    /* private browsing; the setting simply will not persist */
  }
}

interface Blip {
  /** Hz. */
  readonly from: number;
  readonly to: number;
  readonly seconds: number;
  readonly wave: OscillatorType;
}

/**
 * One short swoop.
 *
 * The gain envelope matters more than the pitch: an oscillator started and
 * stopped without one clicks audibly at both ends.
 */
function blip({ from, to, seconds, wave }: Blip, delay = 0): void {
  const ctx = audio();
  if (!ctx) return;

  try {
    const start = ctx.currentTime + delay;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();

    osc.type = wave;
    osc.frequency.setValueAtTime(from, start);
    osc.frequency.exponentialRampToValueAtTime(Math.max(1, to), start + seconds);

    // Quiet on purpose: this plays on a phone held near a face.
    gain.gain.setValueAtTime(0.0001, start);
    gain.gain.exponentialRampToValueAtTime(0.16, start + 0.012);
    gain.gain.exponentialRampToValueAtTime(0.0001, start + seconds);

    osc.connect(gain).connect(ctx.destination);
    osc.start(start);
    osc.stop(start + seconds + 0.02);
  } catch {
    /* see the header: never let a noise become an error */
  }
}

/** A voice per avatar, so picking one is worth doing twice. */
const VOICES: Record<string, () => void> = {
  octopus: () => blip({ from: 420, to: 180, seconds: 0.22, wave: 'sine' }),
  dino: () => {
    blip({ from: 160, to: 70, seconds: 0.3, wave: 'sawtooth' });
    blip({ from: 120, to: 60, seconds: 0.26, wave: 'square' }, 0.07);
  },
  pizza: () => blip({ from: 700, to: 900, seconds: 0.1, wave: 'triangle' }),
  alien: () => {
    blip({ from: 900, to: 1500, seconds: 0.12, wave: 'sine' });
    blip({ from: 1500, to: 700, seconds: 0.16, wave: 'sine' }, 0.1);
  },
  cowboy: () => {
    blip({ from: 500, to: 760, seconds: 0.12, wave: 'triangle' });
    blip({ from: 760, to: 500, seconds: 0.16, wave: 'triangle' }, 0.1);
  },
  flamingo: () => blip({ from: 1200, to: 640, seconds: 0.14, wave: 'sine' }),
  ghost: () => blip({ from: 300, to: 620, seconds: 0.45, wave: 'sine' }),
  robot: () => {
    blip({ from: 220, to: 220, seconds: 0.06, wave: 'square' });
    blip({ from: 380, to: 380, seconds: 0.06, wave: 'square' }, 0.08);
    blip({ from: 300, to: 300, seconds: 0.08, wave: 'square' }, 0.16);
  },
  cat: () => blip({ from: 780, to: 1050, seconds: 0.2, wave: 'sine' }),
  frog: () => {
    blip({ from: 190, to: 130, seconds: 0.1, wave: 'square' });
    blip({ from: 170, to: 110, seconds: 0.12, wave: 'square' }, 0.12);
  },
  unicorn: () => {
    blip({ from: 660, to: 880, seconds: 0.1, wave: 'triangle' });
    blip({ from: 880, to: 1320, seconds: 0.14, wave: 'triangle' }, 0.08);
  },
  taco: () => blip({ from: 340, to: 520, seconds: 0.12, wave: 'triangle' }),
};

export function playAvatar(avatarId: string): void {
  (VOICES[avatarId] ?? (() => blip({ from: 520, to: 700, seconds: 0.1, wave: 'sine' })))();
}

/** Your turn. One rising pair — noticeable, not startling. */
export function playYourTurn(): void {
  blip({ from: 620, to: 780, seconds: 0.1, wave: 'triangle' });
  blip({ from: 820, to: 980, seconds: 0.14, wave: 'triangle' }, 0.09);
}

/** A card leaving your hand. */
export function playCardSound(): void {
  blip({ from: 900, to: 420, seconds: 0.08, wave: 'triangle' });
}

/**
 * One second gone, in the last ten.
 *
 * Dry and very short, because unlike every other sound here this one repeats -
 * ten times in a row, while somebody is trying to think. A square wave clipped
 * to 45ms reads as a click rather than a note, which is what a clock does; the
 * swoops used elsewhere would turn the last ten seconds into a tune.
 *
 * Quiet by the same envelope as the rest, and silent when muted, because
 * `audio()` refuses to hand out a context at all in that case.
 */
export function playTick(): void {
  blip({ from: 1040, to: 820, seconds: 0.045, wave: 'square' });
}

/** The round is over. */
export function playRoundOver(): void {
  blip({ from: 520, to: 660, seconds: 0.12, wave: 'sine' });
  blip({ from: 660, to: 880, seconds: 0.12, wave: 'sine' }, 0.11);
  blip({ from: 880, to: 1180, seconds: 0.22, wave: 'sine' }, 0.22);
}
